import { useCallback, useEffect, useMemo } from 'react'
import uniqBy from 'lodash/uniqBy'
import {
  IGetAutocompleteOptionsConfig,
  IAutocompleteBaseOption,
} from 'transfix-ui/components/AutocompleteBase'
import { useAutocompleteLazyQuery } from 'transfix-ui/components/AutocompleteBase/hooks'
import { IAutocompleteOptionsProps } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteQuery'
import { Maybe } from 'types/graphqlTypes'
import { useDebouncedCallback } from 'use-debounce'
import {
  useLocationSearch,
  ILocationSearchQueryVariables,
  ILocationSearchQuery,
  ILocationSearchItemFragment,
} from '../useLocationSearch'

/**
 * Returns an IAutocompleteBaseOption with text based on fragment
 * @example () => ({ id: '1', text: 'New York, NY', groupIndex: 1 })
 */
const defaultMapOption = ({
  city,
  id,
  stateCode,
}: ILocationSearchItemFragment): IAutocompleteBaseOption<ILocationSearchItemFragment> => ({
  id,
  text: [city, stateCode].filter(Boolean).join(', ') ?? '',
  groupIndex: 1,
})

export interface IUseAutocompleteLocationProps
  extends Partial<IAutocompleteOptionsProps<ILocationSearchQuery>> {
  /**
   * Filter out unwanted searched items
   * @default () => false
   * @example searchString => ['Any Pickup', 'Any Dropoff'].incudes(term)
   */
  filterExcludedSearchString?: (searchString: Maybe<string>) => boolean
  /**
   * Refetch when variable props update
   * @default true
   */
  refetchOnChange?: boolean
  /**
   * Delay before executing first search on input change
   * @default 250
   */
  debounceMs?: number
  /**
   * @example { searchString: 'New York' }
   */
  variables?: Pick<ILocationSearchQueryVariables, 'searchString' | 'resultTypes' | 'countries'>
  /**
   * For autocomplete results group
   * @default 'Locations'
   */
  groupName?: string
  /**
   * Returns a label for each item'
   * @example ({ city: text }, id) => ({ text, id })
   */
  mapOption?: (
    option: ILocationSearchItemFragment,
    index: number
  ) => Omit<IAutocompleteBaseOption, 'groupIndex'>
}

/**
 * Returns a list of options based on query
 * @example
 * ```tsx
 * const { refetch, options = [] } = useAutocopmleteLocation({
 *   getOptionsConfig: data => {
 *     const { geocodingSearch: options = [] } = data || {}
 *     return {
 *       options,
 *       groupName: 'Locations',
 *       groupIndex: 1,
 *       mapOption: ({ city: text }: ILocationItem, id) => ({
 *         id: String(id),
 *         text: text ?? '',
 *       }),
 *     }
 *   },
 * })
 * ```
 */
const useAutocompleteLocation = ({
  debounceMs = 250,
  groupName = undefined,
  mapOption = defaultMapOption,
  value = null,
  variables,
  refetchOnChange = true,
  filterExcludedSearchString: filterExcludedSearchStringProp = () => false,
  ...hookProps
}: IUseAutocompleteLocationProps = {}) => {
  const [_refetchWithoutDebounce, { options: allOptions = [], ...rest }] = useAutocompleteLazyQuery(
    useLocationSearch,
    {
      variables,
    },
    {
      value,
      getOptionsConfig: (
        data: ILocationSearchQuery | undefined
      ): IGetAutocompleteOptionsConfig<ILocationSearchItemFragment> => {
        const { geocodingSearch: options = [] } = data || {}
        return {
          options,
          groupName,
          groupIndex: 1,
          mapOption,
        }
      },
      ...hookProps,
    }
  )

  const refetchWithoutDebounce = useCallback(
    (options: Parameters<typeof _refetchWithoutDebounce>[0]) => {
      const { variables } = options || {}
      const { searchString } = variables || {}
      if (filterExcludedSearchStringProp(searchString)) {
        return
      }

      _refetchWithoutDebounce({
        variables,
      })
    },
    [_refetchWithoutDebounce, filterExcludedSearchStringProp]
  )

  const [refetch] = useDebouncedCallback(refetchWithoutDebounce, debounceMs)

  /**
   * Remove duplicates with the same label
   */
  const options = useMemo(() => {
    return uniqBy(allOptions, 'text') as IAutocompleteBaseOption<ILocationSearchItemFragment>[]
  }, [allOptions])

  /**
   * Refetch when variable prop changes
   */
  useEffect(() => {
    if (refetchOnChange) {
      refetch({ variables })
    }
  }, [refetch, refetchOnChange, variables])

  return {
    ...rest,
    refetch,
    options,
  }
}

export default useAutocompleteLocation
